'use client'

import { create } from 'zustand'

export type ToolGenerationMode = 'full' | 'preview' | 'blocked'

interface CreditState {
  balance: number | null
  quota: number | null
  planName: string | null
  /** Machine plan id (`pro`, `appsumo-t3`, ...). Prefer it over `planName`
   * for any plan-tier decision; display names are not stable identifiers. */
  planSlug: string | null
  /** Null until the balance resolves; see CreditBalance.tool_generation_allowed. */
  toolGenerationAllowed: boolean | null
  /** Null until the balance resolves; see CreditBalance.tool_generation_mode. */
  toolGenerationMode: ToolGenerationMode | null
  lastFetchedAt: number | null
  /** Org slug the cached balance/quota/plan belong to. Used by
   * `useCreditBalance()` to self-check scope on write (see
   * `context-resources-store.ts` for the precedent pattern). */
  orgSlug: string | null
}

interface CreditActions {
  setBalance: (balance: number) => void
  setQuota: (quota: number) => void
  setPlan: (name: string | null, slug?: string | null) => void
  setToolGenerationAllowed: (allowed: boolean | null) => void
  setToolGenerationMode: (mode: ToolGenerationMode | null) => void
  decrementBalance: (amount: number) => void
  setOrgSlug: (orgSlug: string | null) => void
  reset: () => void
}

type CreditStore = CreditState & CreditActions

const initialState: CreditState = {
  balance: null,
  quota: null,
  planName: null,
  planSlug: null,
  toolGenerationAllowed: null,
  toolGenerationMode: null,
  lastFetchedAt: null,
  orgSlug: null,
}

export const useCreditStore = create<CreditStore>()(set => ({
  ...initialState,

  setBalance: balance => set({ balance, lastFetchedAt: Date.now() }),

  setQuota: quota => set({ quota }),

  setPlan: (planName, planSlug = null) => set({ planName, planSlug }),

  setToolGenerationAllowed: toolGenerationAllowed =>
    set({ toolGenerationAllowed }),

  setToolGenerationMode: toolGenerationMode => set({ toolGenerationMode }),

  decrementBalance: amount =>
    set(state => ({
      balance:
        state.balance !== null ? Math.max(0, state.balance - amount) : null,
    })),

  setOrgSlug: orgSlug => set({ orgSlug }),

  reset: () => set(initialState),
}))

// Free-tier orgs have no active subscription, so `quota`
// (credits_per_month) is null and a percentage-based low check can never
// fire. Warn no-subscription orgs once they are down to their last welcome
// credit instead of staying silent until the balance is exhausted.
export const LOW_BALANCE_FALLBACK_THRESHOLD = 2

/**
 * Shared low-balance predicate. Used by the store selector (banner +
 * sidebar indicator) and the billing page, which reads the raw API row,
 * so both surfaces agree on what "low" means — including the free-tier
 * fallback when no monthly quota exists.
 */
export function isBalanceLow(
  balance: number | null,
  quota: number | null,
): boolean {
  if (balance === null || balance <= 0) return false
  // Paid plans: low when under 10% of the monthly quota.
  if (quota !== null && quota > 0) return balance / quota < 0.1
  // Free tier / no quota: fall back to an absolute threshold.
  return balance < LOW_BALANCE_FALLBACK_THRESHOLD
}

// Selectors
export const selectIsExhausted = (state: CreditStore) =>
  state.balance !== null && state.balance <= 0

export const selectIsLow = (state: CreditStore) =>
  isBalanceLow(state.balance, state.quota)

export const selectUsagePercent = (state: CreditStore) => {
  if (state.balance === null || state.quota === null || state.quota <= 0)
    return 0
  return Math.round((state.balance / state.quota) * 100)
}
